import { useEffect, useRef, useState } from 'react'
// W-n / E-n in comments are the review-warning ids of the committed plan,
// docs/superpowers/plans/2026-09-19-cubbo-2818-event-close-reconciliation-wms.md. Phase 6 ids are
// cited as "Phase 6 E-n" and live in docs/superpowers/plans/2026-09-22-cubbo-2819-w2w-signature-wms.md.
import { useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ButtonLoader, Loader } from '../../../components'
import { SHOW_REPLENISHMENT } from '../../../navigation/constants'
import { fetchCoverageEventReconciliation, executeCoverageEvent } from '../../../services/coverageEventServices'
import { coverageEventQueryKeys } from './coverageEventQueryKeys'
import { coverageEventErrorMessage, mapCoverageEventError } from './coverageEventErrors'
import {
  FREEZE_BLOCK_REASONS,
  KINDS,
  PENDING_SIGNATURE_POLL_FACTOR,
  PHASES,
  POLL_MS,
  canExecute,
  canRecordSignature,
  currentPhase,
  displayOutcome,
  furtherAlong,
  hasSignature,
  isParked,
  isStalled,
  mergeEventHeader,
  pickHeader,
  pollInterval,
  shouldRetry,
} from './reconciliationState'
import { foundStockDeepLink } from '../../FoundStock/foundStockLinks'
import { cell, formatTimestamp, makeVocab, outcomeClass, placeholder, separator } from './reconciliationVocab'
import { evidenceUrl } from './evidenceUrl'
import { RecordSignatureDialog } from './RecordSignatureDialog'
import { CoverageEventRemovalCard, REMOVAL_PER_PAGE } from './CoverageEventRemovalCard'
import { TablePaginator } from './TablePaginator'
import { Pill } from './Pill'

// Evidence links are signed redirects valid for an hour from the response that carried them;
// five minutes inside it, so a link the operator clicks right before the cut-over still resolves.
export const EVIDENCE_LINK_TTL_MS = 55 * 60 * 1000
const T = 'cycle_counts.events.reconciliation'
const PER_PAGE = 50
export { POLL_MS, PENDING_SIGNATURE_POLL_FACTOR }
// One place for a column's order and layout, read by its header and its cells alike: identifiers
// never wrap (an operator scans a bay code by shape), quantities right-align with tabular digits.
const COLUMN_CLASS = {
  location: 'text-left whitespace-nowrap',
  kind: 'text-left whitespace-nowrap',
  product: 'text-left whitespace-nowrap',
  lot: 'text-left',
  planned: 'text-right tabular-nums',
  executed: 'text-right tabular-nums',
  outcome: 'text-left',
  reason: 'text-left',
}
const COLUMNS = Object.keys(COLUMN_CLASS)
const banner = 'rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800'
const spinner = (
  <div className="relative h-24">
    <Loader show />
  </div>
)
// The shared Loader is an absolutely positioned overlay, so it needs a sized relative box.

// Server ids only ever become path segments once encoded, the same discipline foundStockDeepLink
// applies to its query params.
const receiptPath = (warehouseId, replenishmentId) =>
  SHOW_REPLENISHMENT.replace(':warehouse_id', encodeURIComponent(String(warehouseId))).replace(':id', encodeURIComponent(String(replenishmentId)))

// Spec §10 says "reuses the Diferencias row rendering"; it does not — a deliberate divergence
// (W-12): the columns (planned/executed/outcome/reason) and the source (the frozen proposal,
// not the count differences) are different, so the tab owns its own table.
// `busy` is the panel's half of the shared flag (`onBusyChange` is this tab's half): a Cancelar
// in flight in the footer must disable Aprobar here, exactly as an approval in flight disables
// Cancelar there — otherwise the two mutations race from one screen (W-5).
// `outcomeUnknown`/`onOutcomeUnknownChange` let the panel hold the "did the execute happen?"
// state across this pane's own remounts (a tab switch); standalone, the pane keeps it itself.
export const CoverageEventReconciliation = ({
  eventId,
  warehouseId,
  event,
  busy = false,
  onChanged,
  onBusyChange,
  onGateOff,
  onRefused,
  outcomeUnknown: heldOutcomeUnknown,
  onOutcomeUnknownChange,
}) => {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [removalPage, setRemovalPage] = useState(1)
  const [localBusy, setLocalBusy] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [recordOpen, setRecordOpen] = useState(false)
  // The dialog signs the proposal it was opened on: a poll that swaps the proposal under the modal
  // must not change what is signed. The platform refuses a captured fingerprint that went stale
  // with PROPOSAL_FINGERPRINT_MISMATCH, which is the fail-closed direction.
  const [recordFingerprint, setRecordFingerprint] = useState(null)
  // A pane unmounted by an event switch still runs its async continuation: nothing it does after
  // that may touch state, and in particular it must not release the NEXT event's busy lock.
  const mounted = useRef(true)
  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )
  const [actionError, setActionError] = useState(null)
  const [notice, setNotice] = useState(null)
  // An older platform has no /reconciliation route at all: one 404 retires the query for good
  // instead of letting the poll hammer a route that will never answer.
  const [unavailable, setUnavailable] = useState(false)
  // An execute that failed without a platform verdict (network, 5xx) may still have executed:
  // Aprobar stays hidden until a real fetch says what the state is.
  const [localOutcomeUnknown, setLocalOutcomeUnknown] = useState(false)
  const outcomeUnknown = onOutcomeUnknownChange ? Boolean(heldOutcomeUnknown) : localOutcomeUnknown
  // The panel's hold is keyed by event and outlives this pane; only the local copy needs a mount.
  const setOutcomeUnknown = value => {
    if (mounted.current) setLocalOutcomeUnknown(value)
    onOutcomeUnknownChange?.(value)
  }
  // Every fetch carries a generation: a fetch that was superseded (a cancel, a newer fetch, an
  // execute that landed after it started) may still resolve — react-query cancels its retryer,
  // never the request — and must not write the header or release the hold.
  const fetchGen = useRef(0)
  // The generation of the last fetch whose answer was accepted (not superseded).
  const acceptedGen = useRef(0)
  // The newest header this pane has seen from a real fetch or an execute response, across pages.
  const latestHeader = useRef(null)
  // The header the pane last rendered (`pickHeader`, a parent further along included), for the
  // mutation continuations: they run in the closure of the render that started them, whose own
  // `header` is the one from before the request went out.
  const shownHeader = useRef(null)
  // Starts from the prop so a mount on the state the panel already knows notifies nobody.
  const lastNotified = useRef(event?.reconciliation_state)
  const outcomeUnknownRef = useRef(false)
  outcomeUnknownRef.current = outcomeUnknown
  const consecutiveFailures = useRef(0)
  const params = { page, per_page: PER_PAGE, removal_page: removalPage, removal_per_page: REMOVAL_PER_PAGE }
  const queryKey = coverageEventQueryKeys.reconciliation(eventId, params)
  // A 404 before any payload means the route does not exist on this platform; one after a
  // payload means the event is gone, which the ordinary error line already describes. Seeded
  // from the cache so a remount over an earlier payload keeps that distinction.
  const everSucceeded = useRef((queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0) > 0)
  // Parked = a standing refusal nothing should re-ask about until the operator retries: every
  // automatic refetch (poll, window focus, reconnect, remount) consults the same predicate.
  const notParked = query => !isParked(query?.state?.error, consecutiveFailures.current)

  const {
    data,
    isLoading,
    isError,
    error: queryError,
    refetch,
    dataUpdatedAt,
  } = useQuery(
    queryKey,
    async () => {
      const gen = (fetchGen.current += 1)
      // The evidence links a payload carries are signed by the platform while it answers this
      // request, so their clock starts when the request went out, not when the answer landed: a
      // frozen or suspended tab can resume long after the links were signed, and the earlier time
      // is the safe side.
      const requestedAt = Date.now()
      const fetched = await fetchCoverageEventReconciliation(eventId, params)
      everSucceeded.current = true
      // Every real fetch (a superseded one too, it is still a real read) stamps that time ON the
      // cached payload, so a remount reads the clock back instead of restarting it, and a header
      // seed (`adoptHeader` spreads the page it relabels) keeps it.
      const payload = fetched ? { ...fetched, linksFetchedAt: requestedAt } : fetched
      if (gen !== fetchGen.current) return payload
      acceptedGen.current = gen
      // Reset here, on a real fetch, never in onSuccess: a local cache write (the execute seed)
      // also fires onSuccess and must not un-park the poll or pass for a platform answer.
      consecutiveFailures.current = 0
      // A full header from the platform replaces the last one (a merge would keep a transient
      // `stalled` for ever); only the execute response, a partial header, is merged.
      if (payload?.event) latestHeader.current = payload.event
      return payload
    },
    {
      enabled: eventId != null && !unavailable,
      // Payload-driven (W-5): the read model's own header decides the cadence, the prop is only
      // the first guess. While a refusal stands there is nothing to poll for: a 401 parks at once,
      // anything else after MAX_CONSECUTIVE_FAILURES; only a successful fetch (the Reintentar
      // control) restarts it. The failure counter lives with the pane, so a tab visit is a
      // deliberate, bounded retry; a cached 401 stays parked across it.
      refetchInterval: (payload, query) =>
        pollInterval({
          header: latestHeader.current ?? payload?.event ?? null,
          parent: event,
          error: query?.state?.error,
          failures: consecutiveFailures.current,
          pollMs: POLL_MS,
        }),
      refetchOnWindowFocus: notParked,
      refetchOnReconnect: notParked,
      refetchOnMount: notParked,
      // …and a remount over a refusal that never had data does not load either.
      retryOnMount: false,
      retry: shouldRetry,
      keepPreviousData: true,
      onError: err => {
        consecutiveFailures.current += 1
        if (err?.response?.status === 404 && !everSucceeded.current) setUnavailable(true)
      },
      onSuccess: payload => {
        // An accepted answer releases the hold on an unknown execute outcome — here, not in the
        // queryFn: react-query dedupes a read-back across a remount, so the answer may arrive
        // through a queryFn that belongs to an earlier, unmounted pane. A cancelled fetch never
        // reaches onSuccess, and the execute seed cannot happen while the hold stands.
        // Belt and braces for the one-microtask window where a superseded fetch resolved before
        // the cancel reached it: only an accepted generation releases the hold.
        if (outcomeUnknownRef.current && acceptedGen.current === fetchGen.current) {
          setOutcomeUnknown(false)
          // Say what the read-back found instead of silently bringing Aprobar back.
          if (canExecute(payload?.event)) setNotice(i18n.t(`${T}.outcome_not_applied`))
        }
        // A dialog hidden because the header stopped allowing a recording (a portal signature, a
        // read-back after an unknown outcome, an execution that started) is closed for good: a
        // later read that allows recording again must not pop it back up under the operator. They
        // open it again themselves. The same header the render gates the dialog on.
        if (mounted.current && !canRecordSignature(pickHeader({ latest: latestHeader.current, cached: payload?.event, parent: event }))) setRecordOpen(false)
        // Once per observed transition (W-5), never once per poll: the panel refetches its own
        // header off this, and the reconciliation header is the fresher of the two.
        const state = payload?.event?.reconciliation_state
        if (state !== lastNotified.current) {
          lastNotified.current = state
          // "Ejecución aprobada" (or a mapped mutation error) describes the state we just left;
          // leaving it under the new one reads as if it had just happened again.
          setNotice(null)
          setActionError(null)
          // One shape for both call sites: `header` only on the execute hand-back (the payload the
          // platform just returned), `state` on every notification. The panel decides from those
          // two what deserves a parent refetch.
          onChanged?.({ state })
        }
        // The proposal can shrink under us (a re-freeze drops rows), stranding the table on a
        // page the server no longer serves. An empty last page is a dead end — go back to 1.
        const totalPages = payload?.meta?.total_pages ?? 0
        if (totalPages > 0 && page > totalPages) setPage(1)
        // Same dead-end guard for the removal document's own pager.
        const removalPages = payload?.removal?.meta?.total_pages ?? 0
        if (removalPages > 0 && removalPage > removalPages) setRemovalPage(1)
      },
    },
  )

  // Re-render once when the evidence links lapse; nothing else in this pane ticks on its own.
  // The clock follows the real fetch that carried the links, read from the stamp on the cached
  // payload (`linksFetchedAt`): the header adoption's `setQueriesData` bumps `dataUpdatedAt`
  // without bringing fresh links, and a remount must not resurrect links that already lapsed. A
  // page without the stamp falls back to `dataUpdatedAt`. The effect still keys on
  // `dataUpdatedAt`, so a real fetch reschedules the timer; no eager re-render on it, a fresher
  // fetch already reads as not expired.
  const linksAt = data?.linksFetchedAt ?? dataUpdatedAt
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!dataUpdatedAt) return undefined
    const remaining = EVIDENCE_LINK_TTL_MS - (Date.now() - linksAt)
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, remaining) + 1)
    return () => clearTimeout(timer)
  }, [dataUpdatedAt, linksAt])

  if (unavailable) return <p className="text-sm text-gray-500">{i18n.t(`${T}.unavailable`)}</p>

  const queryErrorLine = isError ? mapCoverageEventError(queryError, i18n).message : null
  const parked = isParked(queryError, consecutiveFailures.current)
  // Bound to the operator's own retry, not to `isFetching`: the poll would otherwise flip this
  // control to loading every tick while the error banner is up.
  const retry = async () => {
    setRetrying(true)
    try {
      await refetch()
    } finally {
      if (mounted.current) setRetrying(false)
    }
  }
  const retryControl = (
    <ButtonLoader type="secondary" onClick={retry} loading={retrying} disabled={retrying} loading_text={i18n.t(`${T}.loading`)}>
      {i18n.t(`${T}.retry`)}
    </ButtonLoader>
  )
  const pausedNote = parked && (
    <p role="status" className="text-xs font-medium text-gray-600">
      {i18n.t(`${T}.paused`)}
    </p>
  )

  // The hold on an unknown execute outcome is shown in every render state, including the ones
  // with no payload on screen (a remount while the read-back is still pending).
  const outcomeUnknownBanner = outcomeUnknown && (
    <div className={`flex items-center justify-between gap-2 ${banner}`}>
      <p role="alert">{i18n.t(`${T}.outcome_unknown`)}</p>
      {/* One retry control on screen: the error banner offers it when the read-back failed. */}
      {!queryErrorLine && retryControl}
    </div>
  )

  // Three query-render states (W-6): nothing yet + failing, nothing yet + loading, and a
  // failed refresh over rows that are still on screen (handled inside the body below).
  if (queryErrorLine && !data)
    return (
      <div className="space-y-3">
        <p role="alert" className="text-sm text-red-700">
          {queryErrorLine}
        </p>
        {pausedNote}
        {outcomeUnknownBanner}
        <div className="flex">{retryControl}</div>
      </div>
    )
  if (isLoading || !data)
    return (
      <div className="space-y-3">
        {outcomeUnknownBanner}
        {spinner}
      </div>
    )

  const header = pickHeader({ latest: latestHeader.current, cached: data.event, parent: event })
  shownHeader.current = header
  const state = header?.reconciliation_state
  // The rows and counters on screen belong to the cached page's payload; the approval must send
  // THAT proposal's fingerprint, and only while it is still the newest one observed — otherwise
  // the operator confirms one proposal's quantities and the platform executes another's. The
  // newest is the rendered header's, the one the pane trusts: a parent header further along wins
  // there with its own fingerprint (fail closed), so the accept and Registrar wait for the payload
  // to catch up with it.
  const displayedFingerprint = data.event?.proposal_fingerprint
  const newestFingerprint = header?.proposal_fingerprint ?? latestHeader.current?.proposal_fingerprint ?? displayedFingerprint
  const proposalCoherent = displayedFingerprint != null && displayedFingerprint === newestFingerprint
  const counters = data.counters ?? {}
  const rows = data.rows ?? []
  const removal = data.removal ?? null
  // The counters carry `removal` for every event; the tile only makes sense with a document.
  const tileKinds = removal ? PHASES : KINDS
  const meta = data.meta ?? { total_pages: 0, current_page: 1 }
  const phase = currentPhase(counters)
  const computing = state === 'computing'
  const blockedWriteOffUnits = counters.units?.write_off?.blocked ?? 0
  // A signed store-wide proposal carries a removal document; a payload in which a removal is in
  // play (the header's `removal_planned`, removal units in the counters, or rows in the document)
  // must deliver all of it before it can be accepted or signed: the document, its counter and the
  // rows of the page on screen. Missing any of them (a mid-deploy mismatch, a truncated read, an
  // out-of-range page), the operator would sign off on a scope they cannot see (Phase 6 eng review
  // E-5, outside voice). An absent counter is not "nothing to remove": the confirmation's removal
  // sentence reads it. An empty document is the whole scope only when the counter plans no unit
  // (the platform's `removal_planned` is true over an empty pool too, Phase 6 eng review E-3). A
  // count with no rows on the page is transient: the page-reset guard asks for page 1 and the next
  // read delivers. An older platform sends no flag, no counter and no document: nothing is in play.
  // Same posture as `proposalCoherent`: the accept waits for a fresh read.
  const removalCounter = counters.units?.removal ?? null
  const removalItems = data.removal?.meta?.total_count ?? 0
  const removalPlanned = header?.removal_planned === true || (removalCounter?.planned ?? 0) > 0 || removalItems > 0
  const removalDelivered =
    removalCounter != null && data.removal != null && ((removalCounter.planned ?? 0) === 0 || (removalItems > 0 && (data.removal.items?.length ?? 0) > 0))
  const removalCoherent = !removalPlanned || removalDelivered
  // The platform's word on who may record (settler authority, state); gated like the accept on a
  // coherent proposal, a delivered removal document and a known execute outcome — a signature
  // covers the removal scope too.
  const recordOffered = canRecordSignature(header) && proposalCoherent && !outcomeUnknown && removalCoherent
  // The signature is an audit fact that outlives the signed state (it stays on a canceled, an
  // executing and a settled event): rendered wherever the header carries it, styled as the call
  // to action only while the accept is what comes next.
  const signature = hasSignature(header) ? header.signature : null
  const evidence = Array.isArray(signature?.evidence) ? signature.evidence : []
  // Evidence links are signed redirects that die after an hour; a tab left open on a settled or
  // canceled event never refetches on its own, so past the lifetime the anchors give way to a
  // renew control instead of a dead link (Phase 6 eng review E-8, outside voice). `linksAt` is
  // the time of the real fetch that carried the links, stamped on the cached payload so it holds
  // across a header seed and a remount; the timer re-renders once when they lapse.
  const evidenceExpired = evidence.length > 0 && linksAt > 0 && now - linksAt > EVIDENCE_LINK_TTL_MS
  // A signature type this build does not know reads as the neutral sentence, never as a portal
  // signature it may not be.
  const signatureKey = { merchant_portal: 'banner_signed_portal', staff_recorded: 'banner_signed_staff' }[signature?.type] ?? 'banner_signed'
  const signatureLine = signature
    ? i18n.t(`${T}.signature.${signatureKey}`, {
        // A timestamp the platform did not send still reads as a sentence, never "el  por".
        date: formatTimestamp(signature.signed_at, i18n.language) || '—',
        // A signer the platform could not name still reads as a sentence, never "por .".
        name: signature.signed_by?.full_name || '—',
      })
    : null
  // Hand-offs only, on purpose: a blocked hand-off is the one outcome that leaves units behind
  // in Sobrantes, which is what `blocked_hint` and the confirm's blocked sentence describe.
  // Freeze-time blocked units (before execution) or blocked outcomes (after it).
  const blockedHandoffUnits = counters.units?.handoff?.blocked ?? 0
  const blockedHandoffItems = counters.items?.handoff?.blocked ?? 0
  const vocab = makeVocab(i18n, T)
  const reasonLabel = code => (typeof code === 'string' ? vocab('reasons', code) : '')
  // A freeze-time block is worded for what happened before the freeze; the execution-time reason
  // of the same code is a different sentence. Only the codes that have both get the first.
  const blockedLabel = code => (FREEZE_BLOCK_REASONS.includes(code) ? vocab('blocked_reasons', code) : reasonLabel(code))
  // Every bucket the platform sends, not only the outcomes this build knows: a sixth outcome must
  // not shrink the totals or drive the progress line negative.
  const itemCount = kind => Object.values(counters.items?.[kind] ?? {}).reduce((total, n) => total + (Number(n) || 0), 0)
  // Items of the current phase, not bays (W-3): a bay holds several adjustments.
  const phaseTotal = phase ? itemCount(phase) : 0
  // A run that stalled before its first activity write has neither timestamp: say so instead
  // of rendering "desde ." or an Invalid Date.
  const stalledSince = formatTimestamp(header?.last_execution_activity_at ?? header?.execution_approved_at, i18n.language)
  // A stamp that is not a date reads as absent: the "last error" part is left out, never a
  // dangling "Último error: ".
  const lastErrorAt = formatTimestamp(header?.last_execution_error_at, i18n.language)
  const phaseDone = phase ? Math.max(0, phaseTotal - (counters.items?.[phase]?.pending ?? 0)) : 0

  // Both mutations of this pane (the accept and the recorded signature) hand back a header. Order
  // matters. First, a response that lands after the pane already observed a later state (a poll
  // read `executing` or `settled` while the record-signature was in flight, or the panel's header
  // did: the rendered header merges a parent further along) is stale: adopting it would roll the
  // pane, the cache and the panel back. The comparison reads the refs, not this render's `header`:
  // the continuation runs in the closure of the render that sent the request. It seeds nothing and
  // notifies nobody; it only asks for a fresh read. Otherwise: 0) Remember its state as "already
  // notified" so the refetch it triggers does not clear the notice as a fresh transition.
  // 1) Cancel: a fetch already in flight (a focus refetch, or a first fetch of a new page, which
  // `cancelRefetch` alone does not cancel) carries the pre-mutation header — and react-query's
  // cancel REVERTS the query to its pre-fetch snapshot, so a write made before it would be undone.
  // 2) Seed: rendering and polling both read the cached header before the prop, so the response
  // goes into every cached page of the SAME proposal, strictly: a page whose fingerprint equals the
  // response's — the buttons follow the new state and the poll starts even if 3) the invalidate's
  // refetch is slow or fails. Any other page is left as it is: another proposal's, a page read
  // before the freeze (no fingerprint), or every page when the response carries no fingerprint.
  // Relabelling it would pass its rows for the signed proposal. `latestHeader` still merges the
  // response, so the pane's own render follows the new state and `proposalCoherent` withholds the
  // accept on such a page until the invalidate's refetch replaces it. 4) Hand the header to the
  // panel, which applies it before its own refetch lands. Resolves to whether the response was
  // adopted, so a caller never announces an outcome the pane has already moved past.
  const adoptHeader = async resultEvent => {
    if ([latestHeader.current, shownHeader.current].some(seen => seen && furtherAlong(seen, resultEvent))) {
      queryClient.invalidateQueries(coverageEventQueryKeys.reconciliationAny(eventId), {}, { cancelRefetch: true })
      return false
    }
    lastNotified.current = resultEvent?.reconciliation_state ?? lastNotified.current
    await queryClient.cancelQueries(coverageEventQueryKeys.reconciliationAny(eventId))
    if (resultEvent) {
      latestHeader.current = mergeEventHeader(latestHeader.current ?? header, resultEvent)
      const resultFingerprint = resultEvent.proposal_fingerprint
      const sameProposal = old => resultFingerprint != null && old.event?.proposal_fingerprint === resultFingerprint
      // The filter picks the pages; the updater never decides. A react-query write always lands and
      // fires the observer's onSuccess, even when an updater hands `old` (or `undefined`) back: a
      // page left alone that way would replay its stale state as a transition, and a first fetch
      // the cancel above just reverted to "no data" would notify `undefined`.
      queryClient.setQueriesData(
        {
          queryKey: coverageEventQueryKeys.reconciliationAny(eventId),
          predicate: query => query.state.data != null && sameProposal(query.state.data),
        },
        old => ({ ...old, event: mergeEventHeader(old.event, resultEvent) }),
      )
    }
    queryClient.invalidateQueries(coverageEventQueryKeys.reconciliationAny(eventId), {}, { cancelRefetch: true })
    onChanged?.({ header: resultEvent, state: resultEvent?.reconciliation_state })
    return true
  }

  const approve = async () => {
    // Units blocked at freeze never move (a swept write-off, a hand-off the store cannot take), so
    // the confirmation must not promise them (W-4) — for every kind, not only hand-offs now.
    const units = Object.fromEntries(KINDS.map(kind => [kind, Math.max(0, (counters.units?.[kind]?.planned ?? 0) - (counters.units?.[kind]?.blocked ?? 0))]))
    const removalUnits = removalCounter?.planned ?? 0
    const sentences = [
      i18n.t(`${T}.confirm_approve`, units),
      // Only a store-wide close removes LOST, and only when there is something to remove: a
      // narrow event, or a store-wide one over an empty pool, must not read "0 units removed"
      // (Phase 6 eng review E-3: the platform's `removal_planned` is true for an empty document too).
      removalUnits > 0 ? i18n.t(`${T}.confirm_removal`, { count: removalUnits }) : null,
      // A write-off blocked at freeze was swept after the count: nothing to adjust, and nothing
      // in Sobrantes either — its own sentence, apart from the hand-off one (Phase 6 eng review E-7).
      blockedWriteOffUnits > 0 ? i18n.t(`${T}.confirm_blocked_write_off`, { count: blockedWriteOffUnits }) : null,
      blockedHandoffUnits > 0 ? i18n.t(`${T}.confirm_blocked`, { count: blockedHandoffUnits }) : null,
    ].filter(Boolean)
    if (!window.confirm(sentences.join(' '))) return
    setLocalBusy(true)
    onBusyChange?.(true)
    setActionError(null)
    setNotice(null)
    try {
      // The fingerprint is the staleness guard of an irreversible stock write: never ask the
      // platform to execute without it. A header that lost it is a stale proposal — refresh.
      if (!proposalCoherent) {
        const message = coverageEventErrorMessage('PROPOSAL_FINGERPRINT_MISMATCH', i18n)
        setActionError(message)
        onRefused?.(message)
        queryClient.invalidateQueries(coverageEventQueryKeys.reconciliationAny(eventId), {}, { cancelRefetch: true })
        return
      }
      const result = await executeCoverageEvent(eventId, displayedFingerprint)
      // The platform has executed whatever happened to this pane meanwhile (a close, a tab
      // switch): the cache, the invalidation and the parent hand-back are event-keyed and run
      // regardless of the mount; only this pane's own UI writes need it.
      if (mounted.current) setNotice(i18n.t(`${T}.approved`))
      await adoptHeader(result?.event)
    } catch (err) {
      const mapped = mapCoverageEventError(err, i18n, { action: 'execute' })
      // The gate can close between opening the tab and approving; the panel owns the tab-level
      // banner and the Create button, so the refusal has to travel up the same way a drift-add's
      // does instead of dying in this tab's error line.
      if (mapped.gateOff) onGateOff?.()
      // A fingerprint mismatch means the proposal on screen is stale: refresh it once. Nothing
      // re-fires the mutation — the operator approves the refreshed proposal themselves. A network
      // failure or a 5xx is an UNKNOWN outcome (the platform may well have executed): read back
      // the authoritative state before the operator can approve again.
      const unknownOutcome = mapped.code === 'UNEXPECTED'
      if (unknownOutcome) {
        setOutcomeUnknown(true)
        // Nothing that started before this point may answer for it: retire every in-flight fetch
        // (a first fetch of a new page survives `cancelRefetch`) and their late side effects.
        fetchGen.current += 1
        await queryClient.cancelQueries(coverageEventQueryKeys.reconciliationAny(eventId))
      }
      if (mapped.refetch || unknownOutcome) queryClient.invalidateQueries(coverageEventQueryKeys.reconciliationAny(eventId), {}, { cancelRefetch: true })
      if (unknownOutcome) onChanged?.({})
      if (mounted.current) setActionError(mapped.message)
      // The panel keeps the refusal on its own line (outside the tabs) so it outlives this pane
      // whatever lands next — a cancellation, a refetch, a tab switch. When a panel listens, the
      // pane does not render the same sentence a second time. An unknown outcome has its own,
      // more accurate line below, so the generic sentence is not forwarded for it.
      if (!unknownOutcome) onRefused?.(mapped.message)
    } finally {
      // Only a pane still on screen releases the lock: this continuation may belong to an event
      // the panel has already left, whose successor holds its own lock now.
      if (mounted.current) {
        setLocalBusy(false)
        onBusyChange?.(false)
      }
    }
  }

  return (
    <div className="space-y-4">
      {queryErrorLine && (
        <div className="flex items-center justify-between gap-2 rounded bg-red-50 border border-red-200 px-3 py-2">
          <p role="alert" className="text-sm text-red-700">
            {queryErrorLine}
          </p>
          {retryControl}
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">{vocab('state', state)}</span>
        {/* No live role on the ticking counter: it would be re-announced every poll. */}
        {state === 'executing' && phase && (
          <span className="text-xs text-gray-500">{i18n.t(`${T}.progress`, { phase: vocab('phases', phase), done: phaseDone, count: phaseTotal })}</span>
        )}
      </div>
      {/* The numbers stop moving when the poll parks: say so, so Reintentar reads as the way to
          resume rather than a generic retry. */}
      {pausedNote}

      {state === 'pending_approval' && (
        <div className={`flex items-center justify-between gap-2 ${banner}`}>
          {/* The sentence invites recording only when the button is on screen. */}
          <p>{i18n.t(`${T}.${recordOffered ? 'signature_banner_record' : 'signature_banner'}`)}</p>
          {recordOffered && (
            <ButtonLoader
              type="secondary"
              onClick={() => {
                setRecordFingerprint(displayedFingerprint)
                setRecordOpen(true)
              }}
              disabled={localBusy || busy}
            >
              {i18n.t(`${T}.signature.record_button`)}
            </ButtonLoader>
          )}
        </div>
      )}
      {signature && (
        <div
          data-testid="signature-block"
          className={state === 'signed' ? banner : 'rounded bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700'}
        >
          <p role={state === 'signed' ? 'status' : undefined}>{signatureLine}</p>
          {signature.note && <p className="text-xs mt-1">{i18n.t(`${T}.signature.note`, { note: signature.note })}</p>}
          {evidence.length > 0 && (
            <p className="text-xs mt-1 inline-flex flex-wrap items-center gap-x-2">
              <span>{i18n.t(`${T}.signature.evidence`)}</span>
              {/* Resolved on every render from the latest payload: the link is a 60-minute
                  signed redirect, never stored. Past its lifetime the name stays and the link
                  goes, with one control that reads fresh links back. */}
              {evidence.map((file, index) =>
                evidenceExpired ? (
                  <span key={`${file.filename}-${index}`} className="text-gray-500">
                    {file.filename}
                  </span>
                ) : (
                  <a
                    key={`${file.filename}-${index}`}
                    className="text-indigo-700 underline"
                    href={evidenceUrl(file.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {file.filename}
                  </a>
                ),
              )}
              {evidenceExpired && (
                <>
                  <span role="status" className="text-amber-800">
                    {i18n.t(`${T}.signature.evidence_expired`)}
                  </span>
                  <ButtonLoader type="secondary" onClick={retry} loading={retrying} disabled={retrying} loading_text={i18n.t(`${T}.loading`)}>
                    {i18n.t(`${T}.signature.renew_links`)}
                  </ButtonLoader>
                </>
              )}
            </p>
          )}
        </div>
      )}
      {/* The removal scope is part of what is signed and accepted: a payload that lost it can be
          neither. One retry control at a time: a stale proposal has its own banner below, and an
          unknown execute outcome its own line. */}
      {!computing && (canExecute(header) || canRecordSignature(header)) && !removalCoherent && !outcomeUnknown && proposalCoherent && (
        <div className={`flex items-center justify-between gap-2 ${banner}`}>
          <p role="alert">{i18n.t(`${T}.removal_missing`)}</p>
          {!queryErrorLine && retryControl}
        </div>
      )}
      {state === 'executing' && isStalled(header) && (
        <p role="status" className={banner}>
          {stalledSince ? i18n.t(`${T}.stalled`, { since: stalledSince }) : i18n.t(`${T}.stalled_since_unknown`)}
          {lastErrorAt && ` (${i18n.t(`${T}.last_error_at`, { date: lastErrorAt })})`}
        </p>
      )}
      {state === 'executing' && !phase && <p className="text-xs text-gray-500">{i18n.t(`${T}.finalizing`)}</p>}
      {/* `accuracy_window_rebaselined` is only computed on the detail and reconciliation
          headers (W-0): null means "not computed", never "not re-baselined". */}
      {state === 'settled' && header.store_wide && header.accuracy_window_rebaselined && <p className="text-xs text-gray-500">{i18n.t(`${T}.rebaselined`)}</p>}

      {/* The per-kind expansion only exists once the proposal is frozen — all zeros while
          computing, so the grid would be noise (W-3). */}
      {!computing && (
        <div className={`grid grid-cols-1 ${tileKinds.length === 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-2 text-xs text-gray-600`}>
          {tileKinds.map(kind => (
            <div key={kind} className="rounded bg-gray-50 p-2">
              {i18n.t(`${T}.totals`, {
                kind: i18n.t(`${T}.kind.${kind}`),
                planned: counters.units?.[kind]?.planned ?? 0,
                executed: counters.units?.[kind]?.executed ?? 0,
                count: itemCount(kind),
              })}
            </div>
          ))}
        </div>
      )}

      {computing ? (
        <div className="space-y-2">
          {/* A parked poll is not calculating anything the screen will see: no spinner. */}
          {!parked && spinner}
          <p className="text-sm text-gray-500 text-center">
            {i18n.t(`${T}.computing_progress`, { done: counters.rows_computed ?? 0, count: counters.rows_all ?? 0 })}
            {/* The platform stamps the error time when the computation itself is dying: a
                computing event with a stamp is a dead run, not a slow one — Reintentar el
                cálculo in the footer re-enqueues it. */}
            {header?.last_execution_error_at && (
              <span className="block text-amber-800">
                {/* The stamp still marks a dead run when it does not parse: the hint stays, the
                    unreadable time goes. */}
                {lastErrorAt && (
                  <>
                    <span>{i18n.t(`${T}.last_error_at`, { date: lastErrorAt })}</span>
                    {separator}
                  </>
                )}
                <span>{i18n.t(`${T}.computing_error_hint`)}</span>
              </span>
            )}
          </p>
        </div>
      ) : rows.length === 0 ? (
        // A legacy report-only close has no proposal at all; an empty proposal means there was
        // nothing of our own to adjust. Different sentences (W-3).
        <p className="text-sm text-gray-500">{i18n.t(`${T}.${state === 'closed' ? 'no_proposal' : 'none'}`)}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c} className={`px-3 py-1 text-xs text-gray-500 ${COLUMN_CLASS[c]}`}>
                    {i18n.t(`${T}.columns.${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.flatMap(row =>
                (row.items ?? []).map((item, index) => {
                  const outcome = displayOutcome(item)
                  const reason = item.blocked ? blockedLabel(item.blocked) : reasonLabel(item.reason)
                  const unexecuted = item.executed_units != null ? (item.quantity ?? 0) - item.executed_units : 0
                  const showsUnexecuted = outcome !== 'pending' && unexecuted > 0
                  // Hand-offs of one store share one receipt by design (W-8/E-3), so the same link
                  // repeats across the store's rows.
                  const reasonParts = [
                    reason || null,
                    showsUnexecuted ? i18n.t(`${T}.unexecuted_hint`, { count: unexecuted }) : null,
                    item.declaration_id != null ? (
                      <Link className="text-indigo-700 underline" to={foundStockDeepLink(warehouseId, { declaration_id: item.declaration_id })}>
                        {i18n.t(`${T}.view_declaration`)}
                      </Link>
                    ) : null,
                    item.replenishment_id != null ? (
                      <Link className="text-indigo-700 underline" to={receiptPath(warehouseId, item.replenishment_id)}>
                        {i18n.t(`${T}.view_receipt`)}
                      </Link>
                    ) : null,
                  ].filter(Boolean)
                  const hasReasonCell = reasonParts.length > 0
                  // `item.key` is the platform's per-item id; rows mutate live while executing, so a
                  // missing one must not collapse a bay's items onto a single React key.
                  const itemKey = item.key ?? `${item.kind}-${item.product_id}-${item.lot_id ?? '-'}-${index}`
                  return (
                    <tr key={`${row.coverage_event_location_id}-${itemKey}`}>
                      <td className={`${cell} font-mono text-gray-900 ${COLUMN_CLASS.location}`}>{row.location_code}</td>
                      <td className={`${cell} ${COLUMN_CLASS.kind}`}>{vocab('kind', item.kind)}</td>
                      <td className={`${cell} text-gray-900 ${COLUMN_CLASS.product}`}>{item.product_sku ?? `#${item.product_id}`}</td>
                      {/* The platform's proposal items carry the lot id only (no lot_code yet);
                        same empty placeholder as the Diferencias table. */}
                      <td className={`${cell} text-gray-500 ${COLUMN_CLASS.lot}`}>{item.lot_id ?? '-'}</td>
                      <td className={`${cell} ${COLUMN_CLASS.planned}`}>{item.quantity ?? placeholder}</td>
                      <td className={`${cell} ${COLUMN_CLASS.executed}`}>{item.executed_units ?? placeholder}</td>
                      <td className={`${cell} ${COLUMN_CLASS.outcome}`}>
                        <Pill className={outcomeClass(outcome)}>{vocab('outcome', outcome)}</Pill>
                      </td>
                      <td className={`${cell} text-gray-500 ${COLUMN_CLASS.reason}`}>
                        {hasReasonCell ? (
                          <span className="inline-flex flex-wrap items-center gap-x-2">
                            {reasonParts.map((part, index) => (
                              <span key={index} className="whitespace-nowrap">
                                {part}
                                {index < reasonParts.length - 1 && separator}
                              </span>
                            ))}
                          </span>
                        ) : (
                          placeholder
                        )}
                      </td>
                    </tr>
                  )
                }),
              )}
            </tbody>
          </table>
        </div>
      )}

      {!computing && <TablePaginator page={page} meta={meta} onPage={setPage} disabled={localBusy || busy} />}
      {!computing && removal && <CoverageEventRemovalCard removal={removal} page={removalPage} onPage={setRemovalPage} disabled={localBusy || busy} />}
      {/* The cached page describes an older proposal than the one last observed: never offer to
          approve it; a fresh read replaces it. */}
      {!computing && canExecute(header) && !proposalCoherent && !outcomeUnknown && (
        <div className={`flex items-center justify-between gap-2 ${banner}`}>
          <p role="alert">{i18n.t(`${T}.proposal_outdated`)}</p>
          {!queryErrorLine && retryControl}
        </div>
      )}
      {!computing && (blockedHandoffUnits > 0 || blockedHandoffItems > 0) && <p className="text-xs text-gray-500">{i18n.t(`${T}.blocked_hint`)}</p>}
      {actionError && !onRefused && (
        <p role="alert" className="text-sm text-red-700">
          {actionError}
        </p>
      )}
      {outcomeUnknownBanner}
      {notice && (
        <p role="status" className="text-sm text-green-700">
          {notice}
        </p>
      )}
      {canExecute(header) && !outcomeUnknown && proposalCoherent && removalCoherent && (
        <div className="flex justify-end">
          {/* `loading` is this button's own mutation only; the footer's Cancelar in flight merely
              disables it — the irreversible CTA must never announce that it started. */}
          <ButtonLoader type="primary" onClick={approve} loading={localBusy} disabled={localBusy || busy} loading_text={i18n.t(`${T}.loading`)}>
            {i18n.t(`${T}.${hasSignature(header) ? 'accept' : 'approve'}`)}
          </ButtonLoader>
        </div>
      )}
      {recordOpen && canRecordSignature(header) && (
        <RecordSignatureDialog
          open
          eventId={eventId}
          proposalFingerprint={recordFingerprint}
          onCancel={() => setRecordOpen(false)}
          onBusyChange={value => {
            // Only a pane still on screen touches the lock, the parent's half included (as in
            // `approve`): an unmounted pane's successor holds its own lock now.
            if (!mounted.current) return
            setLocalBusy(value)
            onBusyChange?.(value)
          }}
          onGateOff={() => {
            if (mounted.current) setRecordOpen(false)
            onGateOff?.()
          }}
          onRecorded={async result => {
            if (mounted.current) setRecordOpen(false)
            // "Ya puedes aceptar y ejecutar" only over the state it describes: a stale response (the
            // pane already read a later state) is not adopted, and announces nothing.
            const adopted = await adoptHeader(result?.event)
            if (adopted && mounted.current) setNotice(i18n.t(`${T}.signature.recorded`))
          }}
          onRefused={mapped => {
            if (mounted.current) {
              setRecordOpen(false)
              setActionError(mapped.message)
            }
            onRefused?.(mapped.message)
            queryClient.invalidateQueries(coverageEventQueryKeys.reconciliationAny(eventId), {}, { cancelRefetch: true })
          }}
          onOutcomeUnknown={async () => {
            // Resolves once the read-back has landed: the dialog holds its busy flag until then.
            await queryClient.invalidateQueries(coverageEventQueryKeys.reconciliationAny(eventId), {}, { cancelRefetch: true })
          }}
        />
      )}
    </div>
  )
}
